import React from "react"
import {Battery, Zap, Settings, CheckCircle} from 'lucide-react'

const SolarPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-500 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Auto Start Generators for Solar Backup Power
            </h1>
            <p className="text-xl md:text-2xl text-orange-50 leading-relaxed">
              Seamlessly integrate automatic generator starting with your solar inverter system. When battery voltage drops, your generator automatically starts to recharge your battery bank.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How 2-Wire Start Modules Work with Solar Inverters
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Modern solar inverters like Victron and Growatt include built-in relay outputs that activate when your battery voltage falls below a preset threshold. By connecting a 2-wire auto start module to these relay outputs, you can automatically start your backup generator without manual intervention.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              This zero-competition solution is perfect for off-grid homes, RV solar systems, and remote installations where maintaining battery charge is critical. The generator runs only when needed, saving fuel and reducing wear.
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Why Choose Auto Start for Solar Backup?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Battery className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Battery Protection</h3>
              <p className="text-gray-700">
                Prevents deep discharge damage by automatically starting the generator when voltage drops below safe levels.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Unattended Operation</h3>
              <p className="text-gray-700">
                Perfect for remote cabins and off-grid locations where manual generator starting isn't practical.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Settings className="text-orange-500" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Fuel Efficiency</h3>
              <p className="text-gray-700">
                Generator runs only when batteries need charging, minimizing fuel consumption and maintenance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compatible Systems */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Compatible Solar Inverter Systems
            </h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Victron Energy Systems</h3>
                <p className="text-gray-700">
                  Victron MultiPlus and Quattro inverters feature programmable relay outputs specifically designed for generator start control. Configure voltage thresholds and runtime parameters through VictronConnect.
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Growatt Inverters</h3>
                <p className="text-gray-700">
                  Growatt SPF and SPH series off-grid inverters include dry contact relay outputs that can trigger generator starting based on battery state of charge or voltage levels.
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Other Solar Systems</h3>
                <p className="text-gray-700">
                  Most modern off-grid inverters include relay or aux outputs. Check your inverter manual for generator start functionality or contact us for compatibility verification.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Basic Installation Steps
            </h2>
            
            <div className="space-y-4">
              {[
                "Connect the 2-wire start module to your generator's starter circuit",
                "Run control wires from the module to your solar inverter's relay output",
                "Configure voltage thresholds in your inverter settings (typically 22-24V start, 26-28V stop for 24V systems)",
                "Set generator runtime parameters and battery charging profiles",
                "Test the system by manually lowering battery voltage below the threshold"
              ].map((step, index) => (
                <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-semibold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Help Choosing the Right System?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Explore our comprehensive guides for detailed installation instructions and troubleshooting tips
          </p>
        </div>
      </section>
    </div>
  )
}

export default SolarPage
